import React, { useCallback, useEffect, useRef, useState } from 'react';
import './AppNavigation.css';

// Add another object here to create a new top-level tab with its own feature menu.
const NAV_TABS = [
  {
    id: 'tools',
    labelKey: 'nav.tools',
    eyebrowKey: 'nav.tools.eyebrow',
    titleKey: 'nav.tools.title',
    descriptionKey: 'nav.tools.description',
    features: [
      { view: 'words', markKey: 'tool.words.mark', labelKey: 'tool.words.title', descriptionKey: 'tool.words.menuDescription' },
      { view: 'poetry', markKey: 'tool.poetry.mark', labelKey: 'tool.poetry.title', descriptionKey: 'tool.poetry.menuDescription' },
      { view: 'novels', markKey: 'tool.novels.mark', labelKey: 'tool.novels.title', descriptionKey: 'tool.novels.menuDescription' },
      { view: 'cipou', markKey: 'tool.cipou.mark', labelKey: 'tool.cipou.title', descriptionKey: 'tool.cipou.menuDescription' },
      { view: 'classics', markKey: 'classics.mark', labelKey: 'classics.title', descriptionKey: 'classics.menuDescription' },
      { view: 'iching', markKey: 'iching.mark', labelKey: 'iching.title', descriptionKey: 'iching.menuDescription' }
    ]
  },
  {
    id: 'products',
    labelKey: 'nav.products',
    eyebrowKey: 'nav.products.eyebrow',
    titleKey: 'nav.products.title',
    descriptionKey: 'nav.products.description',
    features: [
      { view: 'product-elton', markKey: 'product.elton.mark', labelKey: 'product.elton.label', descriptionKey: 'product.elton.menuDescription' },
      { view: 'product-bookmark', markKey: 'product.bookmark.mark', labelKey: 'product.bookmark.label', descriptionKey: 'product.bookmark.menuDescription' },
      { view: 'product-cards', markKey: 'product.cards.mark', labelKey: 'product.cards.label', descriptionKey: 'product.cards.menuDescription' },
      { view: 'product-reading-notes', markKey: 'product.readingNotes.mark', labelKey: 'product.readingNotes.label', descriptionKey: 'product.readingNotes.menuDescription' }
    ]
  },
  {
    id: 'forum',
    view: 'forum',
    labelKey: 'nav.forum'
  },
  {
    id: 'records',
    labelKey: 'nav.records',
    eyebrowKey: 'nav.records.eyebrow',
    titleKey: 'nav.records.title',
    descriptionKey: 'nav.records.description',
    features: [
      { view: 'reading-notes', markKey: 'notes.mark', labelKey: 'notes.title', descriptionKey: 'notes.menuDescription' },
      { view: 'reading-history', markKey: 'history.mark', labelKey: 'history.title', descriptionKey: 'history.menuDescription' }
    ]
  },
  {
    id: 'settings',
    labelKey: 'nav.settings',
    eyebrowKey: 'nav.settings.eyebrow',
    titleKey: 'nav.settings.title',
    descriptionKey: 'nav.settings.description',
    features: [
      { view: 'settings-language', markKey: 'settings.feature.mark', labelKey: 'settings.feature.label', descriptionKey: 'settings.feature.description' },
      { view: 'settings-appearance', markKey: 'settings.appearance.mark', labelKey: 'settings.appearance.label', descriptionKey: 'settings.appearance.featureDescription' },
      { view: 'settings-references', markKey: 'references.mark', labelKey: 'references.title', descriptionKey: 'references.menuDescription' }
    ]
  },
  {
    id: 'founders-why',
    labelKey: 'nav.foundersWhy',
    eyebrowKey: 'nav.foundersWhy.eyebrow',
    titleKey: 'nav.foundersWhy.title',
    descriptionKey: 'nav.foundersWhy.description',
    features: [
      { view: 'founders-why', markKey: 'nav.foundersWhy.mark', labelKey: 'nav.foundersWhy', descriptionKey: 'nav.foundersWhy.menuDescription' }
    ]
  }
];

export function AppNavigation({ view, onViewChange, t }) {
  const [openTabId, setOpenTabId] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigationRef = useRef(null);
  const activeTabId = NAV_TABS.find(tab =>
    tab.view === view || tab.features?.some(feature => feature.view === view)
  )?.id;
  const openTab = NAV_TABS.find(tab => tab.id === openTabId);
  const closeMenus = useCallback(() => {
    setOpenTabId(null);
    setMobileMenuOpen(false);
  }, []);
  const navigateTo = useCallback((nextView) => {
    closeMenus();
    onViewChange(nextView);
  }, [closeMenus, onViewChange]);
  const toggleMobileMenu = useCallback(() => {
    setOpenTabId(null);
    setMobileMenuOpen(current => !current);
  }, []);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!navigationRef.current?.contains(event.target)) closeMenus();
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeMenus();
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [closeMenus]);

  return (
    <nav className={`app-navigation ${mobileMenuOpen ? 'mobile-menu-open' : ''}`} aria-label={t('nav.main')} ref={navigationRef}>
      <div className="app-navigation-inner">
        <button className={`app-brand ${view === 'home' ? 'active' : ''}`} onClick={() => navigateTo('home')}>
          <span className="app-brand-seal">您</span>
          <span>
            <strong>{t('brand.name')}</strong>
            <small>INTERPOLATE YOU</small>
          </span>
        </button>

        <button
          className="app-navigation-mobile-toggle"
          type="button"
          aria-label={t('nav.main')}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-navigation-tabs"
          onClick={toggleMobileMenu}
        >
          <span aria-hidden="true">{mobileMenuOpen ? '×' : '☰'}</span>
        </button>

        <div className="app-navigation-tabs" id="mobile-navigation-tabs">
          {NAV_TABS.map(tab => (
            <button
              key={tab.id}
              className={activeTabId === tab.id || openTabId === tab.id ? 'active' : ''}
              type="button"
              aria-current={activeTabId === tab.id ? 'page' : undefined}
              aria-expanded={tab.features ? openTabId === tab.id : undefined}
              aria-controls={tab.features ? `navigation-menu-${tab.id}` : undefined}
              onClick={() => tab.view
                ? navigateTo(tab.view)
                : setOpenTabId(current => current === tab.id ? null : tab.id)}
            >
              {t(tab.labelKey)}
              {tab.features && <span className="app-navigation-chevron" aria-hidden="true">⌄</span>}
            </button>
          ))}
        </div>

        <div className="app-navigation-logo-link">
          <img src="/logo-header.png" alt="Interpolate You Logo" className="app-navigation-logo" />
        </div>
      </div>

      {openTab && (
        <div className="app-navigation-menu" id={`navigation-menu-${openTab.id}`}>
          <div className="app-navigation-menu-intro">
            <p>{t(openTab.eyebrowKey)}</p>
            <h2>{t(openTab.titleKey)}</h2>
            <span>{t(openTab.descriptionKey)}</span>
          </div>
          <div className="app-navigation-feature-grid">
            {openTab.features.map(feature => {
              const content = (
                <>
                  <span className="app-navigation-feature-mark" aria-hidden="true">{t(feature.markKey)}</span>
                  <span className="app-navigation-feature-copy">
                    <strong>{t(feature.labelKey)}</strong>
                    <small>{t(feature.descriptionKey)}</small>
                  </span>
                  <span className="app-navigation-feature-arrow" aria-hidden="true">↗</span>
                </>
              );

              return <button key={feature.view} type="button" onClick={() => navigateTo(feature.view)}>{content}</button>;
            })}
          </div>
        </div>
      )}
    </nav>
  );
}
